import { useCallback, useEffect, useState } from 'react';
import { EscalationsServices } from '../../Apis/EscalationsServices';
import { toLowerCase } from '../../constants/commonFunction';
import {
  fetchEscalations,
  getEscalationsState,
} from '../../features/escalationsSlice';
import { useDrawer } from '../../hooks/useDrawer';
import { EscalationStatusType } from '../../pages/Escalations/Escalations';
import { useClientAuth } from '../../providers/ClientProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  EscalationsDto,
  ExpenseEscalationDto,
} from '../../types/escalationsType';
import {
  EscalationStatus,
  EscalationType,
  GetEscalationRequest,
  UpadteEscalationRequest,
} from '../../types/request/escalationsRequest';
import { getErrorMessageFromErrorObj } from '../../utils/api';
import LmFilterSection from '../common/LmFilterSection';
import VegaApproveDialog from '../common/VegaApproveDialog';
import VegaDataGrid from '../common/VegaDataGrid';
import VegaPageContent from '../common/VegaPageContent';
import VegaPageHeader from '../common/VegaPageHeader';
import VegaRejectDialog from '../common/VegaRejectDialog';
import VegaTabBar from '../common/VegaTabBar';
import VegaTabBarItem from '../common/VegaTabItem';
import VegaText from '../common/VegaText';
import { ExpenseManagementColumn } from './Columns/ExpenseManagementColumn';
import { getUserInfo } from './EscalationCommonFunctions';

const ListOfExpenseManagement = () => {
  const { loading, error, escalations, totalItems } =
    useAppSelector(getEscalationsState);
  const dispatch = useAppDispatch();
  const { userId } = useClientAuth();
  const { setSnackbar } = useSnackbar();
  const [userName, setUserName] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [escalationData, setEscalationData] = useState<Partial<EscalationsDto>>(
    {}
  );

  const getReportId = () => {
    const expenseEscalation =
      escalationData.escalationObject as ExpenseEscalationDto;
    const last4Digits = expenseEscalation?.reportId
      ? expenseEscalation.reportId.substring(
          Math.max(0, expenseEscalation.reportId.length - 4)
        )
      : '--';
    return `#${last4Digits}`;
  };

  const {
    open: onApproveOpen,
    close: onApproveClose,
    isOpen: isApproveOpen,
  } = useDrawer();
  const {
    open: onRejectOpen,
    close: onRejectClose,
    isOpen: isRejectOpen,
  } = useDrawer();

  const onApproveDialogOpen = async (escalation: EscalationsDto) => {
    onApproveOpen();
    setEscalationData(escalation);
    const userInfo = await getUserInfo(escalation.userId);
    setUserName(userInfo);
  };
  const onRejectDialogOpen = (escalation: EscalationsDto) => {
    onRejectOpen();
    setEscalationData(escalation);
  };

  const updateEscalation = (
    escalationStatus: EscalationStatus,
    rejectedReason: string,
    onClose: () => void
  ) => {
    const expenseEscalation =
      escalationData.escalationObject as ExpenseEscalationDto;
    const request = {
      id: escalationData.id,
      approverId: expenseEscalation.approverId,
      escalationStatus,
      ...(rejectedReason.length && { rejectedReason }),
    } as UpadteEscalationRequest;

    EscalationsServices.updateEscalation(request)
      .then(() => {
        setSnackbar(`${escalationStatus} successfully !!!`);
        onClose();
        getEscalationList();
      })
      .catch(error => {
        setSnackbar(getErrorMessageFromErrorObj(error), 'error');
        onClose();
      });
  };

  const getEscalationList = useCallback(() => {
    const request = {
      page,
      size,
      managerId: userId,
      escalationType: EscalationType.EXPENSE_REIMBURSEMENT,
      // ...(search.length && { userName: search }),
      ...(selectedStatus === 0
        ? {
            escalationStatus: [EscalationStatus.RAISED],
          }
        : {
            escalationStatus: [
              EscalationStatus.RESOLVED,
              EscalationStatus.REJECTED,
              EscalationStatus.ESCALATED,
            ],
          }),
    } as GetEscalationRequest;
    dispatch(fetchEscalations(request));
  }, [page, size, selectedStatus, dispatch]);

  useEffect(() => {
    const getEscalation = setTimeout(
      getEscalationList,
      search.length ? 300 : 0
    );
    return () => clearTimeout(getEscalation);
  }, [getEscalationList]);

  useEffect(() => {
    if (error) {
      setSnackbar(getErrorMessageFromErrorObj(error), 'error');
    }
  }, [error]);
  return (
    <>
      <VegaPageHeader
        sx={{
          pb: 0,
          bgcolor: 'transparent',
          borderBottom: 'none',
        }}
        renderLeftView={() => {
          return (
            <>
              <VegaTabBar
                value={selectedStatus}
                variant="scrollable"
                scrollButtons={false}
                onChange={(e, selected) => {
                  setSelectedStatus(selected);
                }}
              >
                {Object.keys(EscalationStatusType).map((type: string) => (
                  <VegaTabBarItem key={type} label={toLowerCase(type)} />
                ))}
              </VegaTabBar>
            </>
          );
        }}
        renderRightView={() => {
          return <LmFilterSection setSearch={setSearch} />;
        }}
      />
      <VegaPageContent mt={2}>
        <VegaDataGrid
          idColumn="id"
          data={escalations}
          columns={ExpenseManagementColumn({
            onApproveDialogOpen,
            onRejectDialogOpen,
            selectedStatus,
          })}
          loading={loading}
          page={page}
          pageSize={size}
          rowCount={totalItems}
          onPageChange={page => setPage(page)}
          onPageSizeChange={size => setSize(size)}
        />
      </VegaPageContent>
      <VegaApproveDialog
        header="Are you sure?"
        buttonText="Approve"
        open={isApproveOpen}
        onClose={onApproveClose}
        onApprove={() =>
          updateEscalation(EscalationStatus.RESOLVED, '', onApproveClose)
        }
      >
        <VegaText
          text={`You are approving the request ${getReportId()} of Agent ${userName}`}
          fontWeight={600}
          fontSize={'1.0625rem'}
          color={'black'}
        />
      </VegaApproveDialog>
      <VegaRejectDialog
        text="Reject expense claim?"
        open={isRejectOpen}
        onClose={onRejectClose}
        onReject={(reason: string) =>
          updateEscalation(EscalationStatus.REJECTED, reason, onRejectClose)
        }
      />
    </>
  );
};

export default ListOfExpenseManagement;
